import { type ReactNode, type Ref, type TextareaHTMLAttributes, useId } from "react";
import { alanBicimi, HATA_BICIMI, YARDIM_BICIMI } from "./alan-bicimi";
import { AlanEtiketi } from "./AlanEtiketi";
import { cn } from "./cn";
import { Ikon } from "./Ikon";

export interface MetinAlaniProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  etiket: string;
  /**
   * Etiketi GORSEL olarak gizler — ekran okuyucuda kalir.
   *
   * `Secim`deki ile ayni gerekce: sorunun metni zaten ustte bir
   * `legend` olarak yaziyorsa, alanin ustunde ikinci kez yazmak
   * gurultu olur. Ama etiketi tamamen kaldirmak alani ekran
   * okuyucuda ISIMSIZ birakirdi; o yuzden `etiket` yine ZORUNLU.
   */
  etiketGizli?: boolean;
  /**
   * Alan bos birakilamaz: etiketin yaninda "(zorunlu)" + `aria-required`.
   * Yalnizca GERCEK dogrulama kurali olan alana verilir; arama/suzgec
   * kutularina topluca eklemek isareti anlamsizlastirir. Yerli
   * `required` KOYMUYOR — formlar kendi dogrulamasini yapiyor ve
   * tarayici balonu onun mesajinin onune gecerdi.
   */
  zorunlu?: boolean;
  hata?: string;
  yardim?: ReactNode;
  /** Tek aralikli yazi — sablon metni, JSON, kod */
  daktilo?: boolean;
  /**
   * React 19'da `ref` siradan bir ozellik olarak geciyor ama TIPTE
   * ILAN EDILMEDIKCE derleyici reddediyor. Burada gerekiyor: sablon
   * ekrani degiskeni IMLECIN bulundugu yere ekliyor ve bunun icin
   * `selectionStart` okumasi lazim.
   */
  ref?: Ref<HTMLTextAreaElement>;
}

/**
 * COK SATIRLI METIN ALANI.
 *
 * ================================================================
 * NEDEN AYRI BIR BILESEN GEREKTI
 * ================================================================
 * `Alan` yalnizca `<input>` ciziyor. Cok satirli bir alan gerektigi
 * her yerde geliştirici sinif dizisini ELLE yazdi ve yedi ayri yerde
 * ayni sey tekrarlandi — hepsi de `Alan`in tasidigi iki korumayi
 * KAYBEDEREK:
 *
 *   1. 44px dokunma hedegi (03-kod-standartlari.md, pazarlik disi)
 *   2. 16px yazi boyutu
 *
 * Ikincisi kozmetik degil: iOS Safari, yazi boyutu 16px'in ALTINDA
 * olan bir alana odaklanildiginda sayfayi kendiliginden yakinlastirir
 * ve kullanici yazmayi bitirdiginde uzaklastirmaz. Ekran zipliyor
 * gorunur. Bu proje "mobil oncelikli" ve ogrenci akislari telefonda
 * tek elle kullanilabilmeli.
 *
 * Etiket, hata ve yardim baglantilari `Alan` ile BIREBIR ayni — ve
 * artik bu bir SOZ degil, olcu: ikisi de `alan-bicimi.ts`'ten
 * besleniyor, yani ayrisamiyorlar.
 */
export function MetinAlani({
  etiket,
  etiketGizli,
  zorunlu = false,
  hata,
  yardim,
  daktilo = false,
  className,
  rows = 4,
  readOnly,
  ...rest
}: MetinAlaniProps) {
  const id = useId();
  const hataId = `${id}-hata`;
  const yardimId = `${id}-yardim`;

  return (
    <div className="flex flex-col gap-1.5">
      <AlanEtiketi htmlFor={id} etiket={etiket} gizli={etiketGizli} zorunlu={zorunlu} />

      <textarea
        {...rest}
        id={id}
        rows={rows}
        readOnly={readOnly}
        aria-required={zorunlu || undefined}
        aria-invalid={hata ? true : undefined}
        aria-describedby={cn(hata && hataId, yardim && yardimId) || undefined}
        className={alanBicimi({
          hata: Boolean(hata),
          saltOkunur: Boolean(readOnly),
          // Yuksekligi `rows` veriyor; 44px zaten asiliyor.
          dokunmaHedefi: false,
          ekSinif: cn(daktilo && "font-mono", className),
        })}
      />

      {yardim && (
        <p id={yardimId} className={YARDIM_BICIMI}>
          {yardim}
        </p>
      )}
      {hata && (
        <p id={hataId} role="alert" className={HATA_BICIMI}>
          <Ikon ad="hata" boyut="kucuk" className="mt-px" />
          {hata}
        </p>
      )}
    </div>
  );
}
